import React, { useEffect, useState } from 'react';
import { 
  listWorkflowTaskComments, 
  addWorkflowTaskComment, 
  editWorkflowTaskComment, 
  deleteWorkflowTaskComment 
} from '../services/workflowCommentService';
import { Box, Typography, List, ListItem, ListItemText, TextField, Button, CircularProgress, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

interface WorkflowTaskCommentsProps {
  agencySlug: string;
  propertyId: string;
  workflowId: string;
  stageIndex: number;
  taskIndex: number;
  canEdit?: boolean;
  author?: string;
}

interface Comment {
  _id: string;
  text: string;
  author?: string;
  createdAt: string;
}

const WorkflowTaskComments: React.FC<WorkflowTaskCommentsProps> = ({ 
  agencySlug, 
  propertyId,
  workflowId, 
  stageIndex, 
  taskIndex, 
  canEdit, 
  author 
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchComments = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listWorkflowTaskComments(agencySlug, propertyId, workflowId, stageIndex, taskIndex);
      setComments(data);
    } catch (err: any) {
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workflowId && stageIndex !== undefined && taskIndex !== undefined) fetchComments();
    // eslint-disable-next-line
  }, [workflowId, stageIndex, taskIndex]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await addWorkflowTaskComment(agencySlug, propertyId, workflowId, stageIndex, taskIndex, newComment, author);
      setNewComment('');
      fetchComments();
    } catch (err: any) {
      setError('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingId(comment._id);
    setEditingText(comment.text);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingText.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await editWorkflowTaskComment(agencySlug, propertyId, workflowId, stageIndex, taskIndex, editingId, editingText);
      setEditingId(null);
      setEditingText('');
      fetchComments();
    } catch (err: any) {
      setError('Failed to edit comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async () => {
    if (!deleteId) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteWorkflowTaskComment(agencySlug, propertyId, workflowId, stageIndex, taskIndex, deleteId);
      setDeleteId(null);
      fetchComments();
    } catch (err: any) {
      setError('Failed to delete comment');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ mt: 1, mb: 1, ml: 4 }}>
      <Typography variant="subtitle2" gutterBottom>Task Comments</Typography>
      {loading ? <CircularProgress size={20} /> : (
        <List dense>
          {comments.length === 0 && <ListItem><ListItemText primary="No comments yet." /></ListItem>}
          {comments.map((comment, idx) => (
            <ListItem key={comment._id} alignItems="flex-start"
              secondaryAction={canEdit && (
                <>
                  <IconButton size="small" onClick={() => handleEditComment(comment)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleteId(comment._id)}><DeleteIcon fontSize="small" /></IconButton>
                </>
              )}
            >
              {editingId === comment._id ? (
                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                  <TextField
                    size="small"
                    value={editingText}
                    onChange={e => setEditingText(e.target.value)}
                    fullWidth
                    autoFocus
                  />
                  <Button size="small" variant="contained" onClick={handleSaveEdit} disabled={submitting || !editingText.trim()}>Save</Button>
                  <Button size="small" onClick={() => setEditingId(null)}>Cancel</Button>
                </Box>
              ) : (
                <ListItemText
                  primary={comment.text}
                  secondary={
                    <>
                      {comment.author && <span>{comment.author} &bull; </span>}
                      {new Date(comment.createdAt).toLocaleString((typeof navigator !== 'undefined' && navigator.language) ? navigator.language : undefined)}
                    </>
                  }
                />
              )}
            </ListItem>
          ))}
        </List>
      )}
      
      {canEdit && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField
            size="small"
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            fullWidth
            disabled={submitting}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleAddComment}
            disabled={submitting || !newComment.trim()}
          >
            Add
          </Button>
        </Box>
      )}
      
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Comment</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this comment?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteComment} color="error" disabled={deleting}>Delete</Button>
        </DialogActions>
      </Dialog>
      
      {error && <Typography color="error" variant="caption">{error}</Typography>}
    </Box>
  );
};

export default WorkflowTaskComments; 